import {
  getMinShownDateString,
  getNextAvailableStringDate,
  getAllPossibleDatesByIndex,
  getAllPossibleDatesAsStrings,
} from '../selectors/dateSelectors';
import { END_DATE, START_DATE } from '../constants/dateConstants';
import { getNearestAvailableStringDate } from '../utils/dateUtils';

export const SET_CALENDAR_DATA = 'DATE_PICKER/SET_CALENDAR_DATA';
export const SET_ALL_POSSIBLE_DATES = 'DATE_PICKER/SET_ALL_POSSIBLE_DATES';
export const SET_START_DATE = 'DATE_PICKER/SET_START_DATE';
export const SET_END_DATE = 'DATE_PICKER/SET_END_DATE';
export const SET_DATES = 'DATE_PICKER/SET_DATES';
export const ON_CALENDAR_DATE_SELECTED = 'DATE_PICKER/ON_CALENDAR_DATE_SELECTED';
export const SET_FOCUSED_FIELD = 'DATE_PICKER/SET_FOCUSED_FIELD';
export const SET_IS_DATE_RANGE = 'DATE_PICKER/SET_IS_DATE_RANGE';
export const SET_MAX_DAYS_ADVANCE_FROM_TODAY = 'DATE_PICKER/SET_MAX_DAYS_ADVANCE_FROM_TODAY';
export const SET_IS_DONE_BTN_CLICKED = 'DATE_PICKER/SET_IS_DONE_CLICKED';

export const setIsDoneBtnClicked = (isDoneBtnClicked) => ({
  type: SET_IS_DONE_BTN_CLICKED,
  isDoneBtnClicked,
});

export const setStartDate = ({
  date,
  plusDays,
}) => ({
  type: SET_START_DATE,
  date,
  plusDays,
});
export const setDates = ({
  startDate,
  endDate,
}) => ({
  type: SET_DATES,
  startDate,
  endDate,
});
export const setEndDate = ({
  date,
  plusDays,
}) => ({
  type: SET_END_DATE,
  date,
  plusDays,
});
export const onCalendarDateSelected = (date) => ({
  type: ON_CALENDAR_DATE_SELECTED,
  date,
});
export const setFocusedField = (fieldKey) => ({
  type: SET_FOCUSED_FIELD,
  fieldKey,
});
export const setIsDateRange = (isDateRange) => ({
  type: SET_IS_DATE_RANGE,
  isDateRange,
});

export const setCalendarData = (calendarData) => ({
  type: SET_CALENDAR_DATA,
  calendarData,
});

export const setAllPossibleDates = ({
  allPossibleDatesAsStrings,
  allPossibleDatesByIndex,
}) => ({
  type: SET_ALL_POSSIBLE_DATES,
  allPossibleDatesAsStrings,
  allPossibleDatesByIndex,
});

export const initialDateState = {
  focusedField: null,
  startDate: null,
  endDate: null,
  calendarData: {},
  isDateRange: true,
  isOpen: false,
  isDoneBtnClicked: false,
};

// eslint-disable-next-line complexity -- Disabling as it's cleaner to keep the duck in one fn.
export const dateReducer = (state, action) => {
  switch (action.type) {
    case SET_IS_DONE_BTN_CLICKED: {
      return {
        ...state,
        isDoneBtnClicked: action.isDoneBtnClicked,
      };
    }
    case SET_CALENDAR_DATA: {
      return {
        ...state,
        calendarData: action.calendarData,
      };
    }
    case SET_ALL_POSSIBLE_DATES: {
      return {
        ...state,
        allPossibleDatesAsStrings: action.allPossibleDatesAsStrings,
        allPossibleDatesByIndex: action.allPossibleDatesByIndex,
      };
    }
    case SET_MAX_DAYS_ADVANCE_FROM_TODAY: {
      return {
        ...state,
        maxDaysAdvanceFromToday: action.maxDaysAdvanceFromToday,
      };
    }
    case SET_DATES: {
      return {
        ...state,
        startDate: action.startDate,
        endDate: action.endDate,
      };
    }
    case SET_FOCUSED_FIELD:
      return {
        ...state,
        focusedField: action.fieldKey,
      };
    case SET_IS_DATE_RANGE:
      return {
        ...state,
        isDateRange: !!action.isDateRange,
      };
    case ON_CALENDAR_DATE_SELECTED: {
      const {
        focusedField,
        isDateRange,
      } = state;
      // focusedField is null for inline date-picker as focus is not set to start date at first
      if (focusedField === null) {
        return {
          ...state,
          startDate: action.date,
          endDate: null,
          focusedField: END_DATE,
        };
      }
      return {
        ...state,
        ...focusedField === START_DATE && {
          startDate: action.date,
          endDate: null,
        },
        ...isDateRange && focusedField === END_DATE && {
          endDate: action.date,
        },
        focusedField: isDateRange && focusedField === START_DATE ? END_DATE : START_DATE,
      };
    }
    case SET_START_DATE: {
      const {
        startDate,
      } = state;
      const {
        date = startDate,
        plusDays = null,
      } = action;
      if (!date) {
        return state;
      }
      let newStartDateString = date;
      if (plusDays) {
        const allPossibleDatesByIndex = getAllPossibleDatesByIndex({ state });
        const allPossibleDatesAsStrings = getAllPossibleDatesAsStrings({ state });
        const originalDateIndex = allPossibleDatesAsStrings[newStartDateString];
        const nextDateIndex = originalDateIndex + plusDays;
        newStartDateString = allPossibleDatesByIndex[nextDateIndex];
        newStartDateString = getNearestAvailableStringDate(state, plusDays > 0, newStartDateString);
      } else {
        newStartDateString = getNearestAvailableStringDate(state, true, newStartDateString);
      }
      return {
        ...state,
        startDate: newStartDateString || startDate,
        endDate: null,
      };
    }
    case SET_END_DATE: {
      const {
        endDate,
        startDate,
      } = state;
      const {
        date = endDate,
        plusDays = null,
      } = action;
      if (!date) {
        const targetStartDateString = startDate || getMinShownDateString({ state });
        const nextAvailableDateString = getNextAvailableStringDate(
          { state },
          targetStartDateString
        );
        return {
          ...state,
          endDate: nextAvailableDateString || endDate,
        };
      }
      let newEndDateString = date;
      if (plusDays) {
        // TODO refactor this date logic out
        const allPossibleDatesByIndex = getAllPossibleDatesByIndex({ state });
        const allPossibleDatesAsStrings = getAllPossibleDatesAsStrings({ state });
        const originalDateIndex = allPossibleDatesAsStrings[newEndDateString];
        const nextDateIndex = originalDateIndex + plusDays;
        newEndDateString = allPossibleDatesByIndex[nextDateIndex];
        newEndDateString = getNearestAvailableStringDate(state, plusDays > 0, newEndDateString);
      } else {
        newEndDateString = getNearestAvailableStringDate(state, false, newEndDateString);
      }
      if (!newEndDateString) {
        return state;
      }
      return {
        ...state,
        endDate: newEndDateString,
      };
    }
    default:
      return state;
  }
};
